// Leprecon+ CRM · Caliper Cover Color Palette
// -----------------------------------------------------------------------------
// 17 canonical colors sourced from the user's official color chart
// (Chinese-language caliper cover poster) + 14-color English marketing poster.
// Every catalog / order-line color swatch and picker in the app should draw
// from this single list. Add ALIASES (case-insensitive) for legacy DB values
// so old orders continue to render with the correct swatch.
// -----------------------------------------------------------------------------

export const COLORS = [
  { name: "Orange",             hex: "#E56A2A", zh: "橙色",     aliases: ["orange"] },
  { name: "White",              hex: "#FFFFFF", zh: "白色",     aliases: ["white", "alpine white", "pearl white"] },
  { name: "Tiffany Blue",       hex: "#5FC8D1", zh: "蒂芙尼蓝", aliases: ["tiffany blue", "lt. cyan", "light cyan", "lt.cyan", "cyan"] },
  { name: "Fluorescent Yellow", hex: "#D4F53F", zh: "荧光黄",   aliases: ["fluorescent yellow", "fluor yellow", "neon yellow"] },
  { name: "Light Green",        hex: "#86EFAC", zh: "浅绿",     aliases: ["light green", "lt. green", "lt.green", "pale green"] },
  { name: "Red",                hex: "#D8241C", zh: "红色",     aliases: ["red"] },
  { name: "Flash Gold",         hex: "#D4A82F", zh: "闪金",     aliases: ["flash gold", "gold", "glitter gold"] },
  { name: "Rose Gold",          hex: "#B77E6B", zh: "玫瑰金",   aliases: ["rose gold", "pr. pink", "premium pink"] },
  { name: "Purple",             hex: "#7A3EA0", zh: "紫色",     aliases: ["purple"] },
  { name: "Emerald Green",      hex: "#4EA83A", zh: "翠绿",     aliases: ["emerald green", "emerald", "dk. green", "dark green", "green"] },
  { name: "Silver",             hex: "#B8B9BB", zh: "银色",     aliases: ["silver"] },
  { name: "Blue",               hex: "#2E6FD4", zh: "蓝色",     aliases: ["blue"] },
  { name: "Light Blue",         hex: "#3E86D0", zh: "浅蓝",     aliases: ["light blue", "lt. blue", "lt.blue", "sky blue"] },
  { name: "Dark Blue",          hex: "#1B3D8F", zh: "深蓝",     aliases: ["dark blue", "dk. blue", "navy", "navy blue"] },
  { name: "Yellow",             hex: "#F5CA1E", zh: "黄色",     aliases: ["yellow"] },
  { name: "Pink",               hex: "#E23F8F", zh: "粉色",     aliases: ["pink"] },
  { name: "Grey",               hex: "#4C5158", zh: "灰色",     aliases: ["grey", "gray"] },
  { name: "Matte Black",        hex: "#2A2E33", zh: "哑光黑色", aliases: ["matte black", "flat black"] },
  { name: "Black",              hex: "#0A0A0A", zh: "黑色",     aliases: ["black", "gloss black"] },
];

// name (canonical) or any alias → COLORS entry
const _LOOKUP = (() => {
  const m = new Map();
  for (const c of COLORS) {
    m.set(c.name.toLowerCase(), c);
    for (const a of c.aliases) m.set(a.toLowerCase(), c);
  }
  return m;
})();

/**
 * Look up the canonical color record for a free-text name (case & spacing
 * insensitive). Returns null when unrecognized so callers can fall back to
 * a neutral swatch instead of a misleading match.
 */
export function findColor(name) {
  if (!name) return null;
  const key = String(name).trim().toLowerCase().replace(/\s+/g, " ");
  return _LOOKUP.get(key) || null;
}

/**
 * Return the best hex for a color name. Falls back to a neutral swatch grey
 * (#D4D4D8) when the name doesn't match any canonical color or its aliases —
 * this lets the UI still render a small circle without silently pretending an
 * unknown name is a specific color.
 */
export function colorToHex(name) {
  const c = findColor(name);
  return c ? c.hex : "#D4D4D8";
}

/**
 * The 17 canonical color names, in the display order used on the paint chart.
 * Useful for dropdowns / pickers.
 */
export const COLOR_NAMES = COLORS.map((c) => c.name);
